import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { Building2, FileDown, Trophy, CalendarRange } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { PeriodPicker, periodLabel, prevLabel } from "./PeriodPicker";
import { fmtD } from "../lib/date";
import { ink } from "../lib/ink";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const short = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return "$" + (v / 1e6).toFixed(2) + "M";
  if (v >= 1e3) return "$" + (v / 1e3).toFixed(0) + "k";
  return "$" + v;
};
const COLORS = { la: "#4338ca", norcal: "#b45309", sd: "#15803d" };
const RANK_STYLE = { 1: "bg-amber-400 text-amber-900", 2: "bg-slate-300 text-slate-700", 3: "bg-orange-300 text-orange-900" };
const pct = (cur, prev) => (prev > 0 ? Math.round(((cur - prev) / prev) * 100) : null);

export default function BranchPerformance({ period: ctrlPeriod, onPeriodChange }) {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [localPeriod, setLocalPeriod] = useState(() => localStorage.getItem("bp_period") || "all");
  const period = ctrlPeriod || localPeriod;
  const setPeriod = (k) => { setLocalPeriod(k); if (onPeriodChange) onPeriodChange(k); };
  const [rows, setRows] = useState([]);
  const [crm, setCrm] = useState([]);
  const [crmTrend, setCrmTrend] = useState(null);
  const [metric, setMetric] = useState("collected");
  const [openRegion, setOpenRegion] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProj, setLoadingProj] = useState(false);
  const requestSeq = useRef(0);
  const [projectError, setProjectError] = useState(false);
  const drilldownRef = useRef(null);
  const isAll = period === "all";
  const params = { params: { months: 6, period: isAll ? "" : period } };

  useEffect(() => { localStorage.setItem("bp_period", period); }, [period]);
  useEffect(() => {
    if (!openRegion) return;
    // 明细区位于趋势图之后；展开分公司卡片时滚到明细标题，避免用户误以为点击无响应。
    const frame = window.requestAnimationFrame(() => {
      drilldownRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [openRegion]);
  useEffect(() => {
    const seq = ++requestSeq.current;
    Promise.allSettled([
      api.get("/analytics/branch-performance", params),
      api.get("/crm/analytics/companies", params),
      api.get("/crm/analytics/companies/trend", params),
    ]).then(([branchResult, crmResult, trendResult]) => {
      if (seq !== requestSeq.current) return;
      setRows(branchResult.status === "fulfilled" ? (branchResult.value.data || []) : []);
      setCrm(crmResult.status === "fulfilled" ? (crmResult.value.data || []) : []);
      setCrmTrend(trendResult.status === "fulfilled" ? trendResult.value.data : null);
    });
    return () => { requestSeq.current += 1; };
  }, [api, period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (openRegion) loadProjects(openRegion); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadProjects = async (region) => {
    setLoadingProj(true); setProjects([]); setProjectError(false);
    try { const { data } = await api.get(`/analytics/branch-performance/${region}/projects`, params); setProjects(data || []); }
    catch { setProjectError(true); }
    finally { setLoadingProj(false); }
  };
  const toggleRegion = async (region) => {
    if (openRegion === region) { setOpenRegion(null); return; }
    setOpenRegion(region);
    await loadProjects(region);
  };

  const exportBranch = async () => {
    if (!openRegion) return;
    try {
      const { data } = await api.get(`/analytics/branch-performance/${openRegion}/export`, { ...params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data])); const a = document.createElement("a");
      a.href = url; a.download = `branch_${openRegion}${isAll ? "" : `_${period}`}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  if (!rows.length) return null;

  const METRICS = [
    { k: "collected", zh: "回款", en: "Collected", src: "cons" },
    { k: "signed", zh: "签单额", en: "Signed", src: "cons" },
    { k: "leads", zh: "新增客户", en: "New Leads", src: "crm" },
  ];
  const axis = (rows[0]?.monthly || []).map((m) => m.label || m.month.slice(2));
  const visibleRegions = rows.map((r) => r.region).filter(Boolean); // 区域经理只返回本分公司 → 图例只画本分公司
  const trendData = metric === "leads"
    ? (crmTrend?.months || []).map((m, idx) => { const row = { month: crmTrend?.labels?.[idx] || m.slice(2) }; (crmTrend?.series || []).forEach((s) => { row[s.region] = s.data[idx]?.leads || 0; }); return row; })
    : axis.map((lab, idx) => { const row = { month: lab }; rows.forEach((r) => { row[r.region] = r.monthly[idx]?.[metric] || 0; }); return row; });
  const crmBy = Object.fromEntries(crm.map((c) => [c.region, c]));
  const isMoney = metric !== "leads";
  const range = rows[0]?.range;
  const trendTitle = isAll ? (zh ? "近 6 个月发展趋势（谁在加速）" : "6-Month Trend")
    : `${periodLabel(period, zh)}${zh ? (rows[0]?.gran === "day" ? "逐日走势" : "逐月走势") : (rows[0]?.gran === "day" ? " daily trend" : " monthly trend")}`;

  return (
    <div data-testid="branch-performance-scoreboard" className="mb-6">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-brand-500" />
          {zh ? "分公司业绩榜 · 三公司对比（独立运营）" : "Branch Scoreboard · Three-Company Comparison"}
          <span className="text-xs font-normal text-slate-400 hidden sm:inline">{zh ? "签单额 · 回款 · 回款率 · CRM 获客，点击卡片查看项目明细" : "Signed · Collected · Rate · CRM, click for detail"}</span>
        </h3>
        <div className="ml-auto flex items-center gap-2">
          {range && <span className="text-[11px] text-slate-400 inline-flex items-center gap-1" data-testid="branch-period-range"><CalendarRange className="h-3 w-3" />{fmtD(range.start)} ~ {fmtD(range.end)}</span>}
          <PeriodPicker value={period} onChange={setPeriod} zh={zh} testid="branch-period" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {rows.map((r) => {
          const c = crmBy[r.region] || {};
          const momArr = r.monthly || [];
          const cur = isAll ? (momArr[momArr.length - 1]?.collected || 0) : r.collected;
          const prev = isAll ? (momArr[momArr.length - 2]?.collected || 0) : r.prevCollected;
          const mom = pct(cur, prev);
          return (
            <div key={r.region} data-testid={`branch-card-${r.region}`} onClick={() => toggleRegion(r.region)}
              className={`bg-white rounded-xl border p-4 relative overflow-hidden cursor-pointer transition-colors ${openRegion === r.region ? "border-red-300 ring-1 ring-red-200" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="absolute top-0 left-0 w-full h-1" style={{ background: COLORS[r.region] }} />
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                  <span data-testid={`branch-rank-${r.region}`} className={`h-5 w-5 rounded-full text-[11px] font-extrabold flex items-center justify-center ${RANK_STYLE[r.rank] || "bg-slate-100 text-slate-500"}`}>
                    {r.rank === 1 ? <Trophy className="h-3 w-3" /> : r.rank}
                  </span>
                  {zh ? r.name : r.nameEn}
                </p>
                <span className="text-[11px] px-2 py-0.5 rounded-full" style={ink(COLORS[r.region], true)} data-testid={`branch-projects-${r.region}`}>{r.projects} {isAll ? (zh ? "项目" : "projects") : (zh ? "期内签单" : "signed")}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                <div>
                  <p className="text-[11px] text-slate-400">{zh ? (isAll ? "签单额" : "期内签单额") : "Signed"}</p>
                  <p className="text-lg font-bold text-slate-800" data-testid={`branch-signed-${r.region}`}>{short(r.signedValue)}</p>
                  {!isAll && <p className="text-[10px] text-slate-400" data-testid={`branch-signed-prev-${r.region}`}>{prevLabel(period, zh)} {pct(r.signedValue, r.prevSigned) === null ? "—" : `${pct(r.signedValue, r.prevSigned) >= 0 ? "+" : ""}${pct(r.signedValue, r.prevSigned)}%`}</p>}
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">{zh ? (isAll ? "已回款" : "期内回款") : "Collected"}</p>
                  <p className="text-lg font-bold text-green-600" data-testid={`branch-collected-${r.region}`}>{short(r.collected)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">{isAll ? (zh ? "回款环比" : "MoM") : `${zh ? "回款" : "Collected "}${prevLabel(period, zh)}`}</p>
                  <p className={`text-lg font-bold ${mom === null ? "text-slate-300" : mom >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`branch-mom-${r.region}`}>{mom === null ? "—" : `${mom >= 0 ? "+" : ""}${mom}%`}</p>
                </div>
              </div>
              <div className="mt-2">
                <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                  <span>{zh ? (isAll ? "回款率" : "期内回款 / 期内签单") : "Collection rate"}</span>
                  <span data-testid={`branch-rate-${r.region}`} className="font-semibold" style={ink(COLORS[r.region])}>{r.collectRate}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden" title={money(r.collected)}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, r.collectRate)}%`, background: COLORS[r.region] }} />
                </div>
              </div>
              <div className="-mx-2 mt-1">
                <ResponsiveContainer width="100%" height={36}>
                  <AreaChart data={momArr} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`bp-${r.region}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS[r.region]} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={COLORS[r.region]} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="collected" stroke={COLORS[r.region]} strokeWidth={1.5} fill={`url(#bp-${r.region})`} dot={false} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-1 text-[11px] text-slate-500" data-testid={`branch-crm-${r.region}`}>
                <span>{zh ? "新增客户" : "Leads"} <b className="text-slate-700">{c.leads ?? "—"}</b></span>
                <span>{zh ? "签约数" : "Signed"} <b className="text-green-600">{c.signed ?? "—"}</b></span>
                <span>{zh ? "转化率" : "Conv."} <b className="text-indigo-600">{c.conversion ?? "—"}%</b></span>
              </div>
              <p className="mt-1 text-[10px] text-slate-400" data-testid={`branch-crm-basis-${r.region}`}>
                {zh ? "新客按实际见面或完成现场评估日期；公海领取和预约不计。签约按实际签订日期。" : "New customers use actual meeting or completed evaluation dates; pool claims and bookings do not count. Signed uses actual contract date."}
              </p>
              {((c.activationNeedsReview || 0) > 0 || (c.signedDateNeedsReview || 0) > 0) && <p className="mt-1 text-[10px] font-medium text-amber-700" data-testid={`branch-crm-review-${r.region}`}>
                {zh ? `历史日期待核（未计入）：激活 ${c.activationNeedsReview || 0} · 签约 ${c.signedDateNeedsReview || 0}` : `Historical dates to verify (excluded): activation ${c.activationNeedsReview || 0} · signed ${c.signedDateNeedsReview || 0}`}
              </p>}
              <p className="text-[10px] text-red-500 mt-1.5 font-medium">{openRegion === r.region ? (zh ? "▲ 收起明细" : "▲ Hide") : (zh ? "▼ 点击展开项目明细" : "▼ View projects")}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mt-3" data-testid="branch-trend-chart">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-800" data-testid="branch-trend-title">{trendTitle}</p>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="branch-metric-toggle">
            {METRICS.map((o) => (
              <button key={o.k} data-testid={`branch-metric-${o.k}`} onClick={() => setMetric(o.k)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${metric === o.k ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                {zh ? o.zh : o.en}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={210}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} tickFormatter={(v) => (isMoney ? `$${(v / 1000).toFixed(0)}k` : v)} />
            <Tooltip formatter={(v) => (isMoney ? money(v) : v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {[["norcal", zh ? "北加州" : "NorCal", "#f59e0b"], ["la", zh ? "洛杉矶" : "LA", "#6366f1"], ["sd", zh ? "圣地亚哥" : "SD", "#16a34a"]]
              .filter(([k]) => !visibleRegions.length || visibleRegions.includes(k))
              .map(([k, name, color]) => <Line key={k} type="monotone" dataKey={k} name={name} stroke={color} strokeWidth={2} dot={{ r: 3 }} />)}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {openRegion && (
        <div ref={drilldownRef} data-testid={`branch-drilldown-${openRegion}`} className="bg-white rounded-xl border border-slate-200 mt-3 scroll-mt-20 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">{zh ? `${(rows.find((x) => x.region === openRegion) || {}).name} · 项目签单与回款明细${isAll ? "" : `（${periodLabel(period, zh)}：期内签单或期内有回款）`}` : `Projects${isAll ? "" : ` · ${periodLabel(period, zh)}`}`}</span>
            <button data-testid={`branch-export-${openRegion}`} onClick={exportBranch}
              className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50">
              <FileDown className="h-3.5 w-3.5" />{zh ? "导出 Excel" : "Export"}
            </button>
          </div>
          {loadingProj ? <p className="px-4 py-4 text-sm text-slate-400">{zh ? "加载中…" : "Loading…"}</p> : projectError ? (
            <div className="px-4 py-6 text-sm text-center">
              <p className="text-red-500">{zh ? "项目明细加载失败，请重试。" : "Project details failed to load. Please retry."}</p>
              <button onClick={() => loadProjects(openRegion)} className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800">
                {zh ? "重新加载" : "Retry"}
              </button>
            </div>
          ) : projects.length === 0 ? <p className="px-4 py-6 text-sm text-slate-400 text-center" data-testid="branch-drilldown-empty">{zh ? "该时间范围内无签单 / 回款记录" : "No signed projects or collections in this period"}</p> : (
            <div className="max-h-96 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0"><tr>
                  <th className="text-left px-4 py-2">{zh ? "项目地址" : "Address"}</th>
                  <th className="text-left px-4 py-2">{zh ? "业主" : "Owner"}</th>
                  <th className="text-left px-4 py-2">{zh ? "销售" : "Sales"}</th>
                  <th className="text-left px-4 py-2">{zh ? "阶段" : "Phase"}</th>
                  <th className="text-right px-4 py-2">{zh ? "签单额" : "Contract"}</th>
                  <th className="text-right px-4 py-2">{zh ? (isAll ? "已回款" : "期内回款") : (isAll ? "Collected" : "Period collected")}</th>
                  {!isAll && <th className="text-right px-4 py-2">{zh ? "累计回款" : "Total collected"}</th>}
                  <th className="text-right px-4 py-2">{zh ? "回款率" : "Rate"}</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {projects.map((p) => (
                    <tr key={p.id} data-testid={`branch-proj-${p.id}`} data-signed-in-period={p.signedInPeriod ? "1" : "0"}>
                      <td className="px-4 py-2 text-slate-800">{p.address}{!isAll && p.signedInPeriod && <span className="ml-1.5 text-[10px] font-semibold text-red-600 bg-red-50 border border-red-100 rounded px-1">{zh ? "期内签单" : "signed"}</span>}</td>
                      <td className="px-4 py-2 text-slate-600">{p.clientName || "-"}</td>
                      <td className="px-4 py-2 text-slate-600">{p.sales || "-"}</td>
                      <td className="px-4 py-2 text-slate-500">{p.phase || "-"}</td>
                      <td className="px-4 py-2 text-right font-medium text-slate-800">{money(p.contractAmount)}</td>
                      <td className="px-4 py-2 text-right text-green-600">{money(p.collected)}</td>
                      {!isAll && <td className="px-4 py-2 text-right text-slate-500">{money(p.collectedTotal)}</td>}
                      <td className="px-4 py-2 text-right font-semibold" style={ink(COLORS[openRegion])}>{p.collectRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
